/**
 * Itinerary Validators
 *
 * Parameterized validation functions extracted from WanderAppComplete.jsx.
 * These were originally closure-dependent; now accept explicit parameters for testability.
 */

import { KNOWN_CITIES, CITY_TO_COUNTRY, UNREALISTIC_DAY_TRIP_PAIRS, DISTANT_LOCATION_PATTERNS } from './itineraryConstants.js';

// ============================================
// IS HOTEL ACTIVITY
// ============================================

/**
 * Determines if an activity/meal is at the hotel (to skip transit between hotel-based items).
 *
 * @param {string} location - Activity/meal location
 * @param {string} activity - Activity name
 * @param {string} restaurant - Restaurant name
 * @param {string} hotelName - Hotel name for this day
 * @param {string} baseCity - Base city for this day
 * @returns {boolean} True if the item is at the hotel
 */
export const isHotelActivity = (location, activity, restaurant, hotelName, baseCity, address) => {
  const locLower = (location || '').toLowerCase();
  const actLower = (activity || '').toLowerCase();
  const restLower = (restaurant || '').toLowerCase();
  const hotelLower = (hotelName || '').toLowerCase();
  const addrLower = (address || '').toLowerCase();

  if (!hotelLower) return false;

  // Check if address contains hotel name (e.g., "Miraval Berkshires Resort, 55 Lee Road")
  if (addrLower && addrLower.includes(hotelLower)) return true;
  if (addrLower && hotelLower.length > 5 && addrLower.includes(hotelLower.replace(/^the\s+/, ''))) return true;

  // Check if location/restaurant mentions hotel name exactly
  if (locLower.includes(hotelLower) || restLower.includes(hotelLower)) return true;

  // Also check without "The" prefix
  if (hotelLower.length > 5 && locLower.includes(hotelLower.replace(/^the\s+/, ''))) return true;

  // Check if location explicitly says "at hotel" or "hotel"
  if (locLower === 'hotel' || locLower === 'at hotel' || locLower.includes('at the hotel')) return true;

  // Check for resort/hotel-based activities
  if (actLower.includes('at hotel') || actLower.includes('hotel spa') || actLower.includes('hotel pool') ||
      actLower.includes('hotel gym') || actLower.includes('hotel fitness')) return true;

  // Check if location is the same as the hotel location (for resorts)
  if (locLower.includes('resort') && hotelLower.includes('resort')) return true;

  // Check for St. Regis specifically
  if (hotelLower.includes('st. regis') || hotelLower.includes('st regis')) {
    if (locLower.includes('st. regis') || locLower.includes('st regis')) return true;
  }

  // Extract key words from hotel name, EXCLUDING city/country names to prevent false positives
  const baseCityLower = (baseCity || '').toLowerCase().trim();
  const baseCityWords = baseCityLower.split(/[\s,]+/).filter(w => w.length > 2);
  const countryNames = ['japan', 'france', 'italy', 'spain', 'germany', 'thailand', 'indonesia', 'vietnam',
    'mexico', 'brazil', 'argentina', 'australia', 'china', 'korea', 'india', 'turkey', 'greece', 'portugal',
    'morocco', 'egypt', 'peru', 'colombia', 'chile', 'england', 'scotland', 'ireland', 'switzerland',
    'austria', 'netherlands', 'belgium', 'norway', 'sweden', 'denmark', 'finland', 'iceland', 'croatia',
    'czech', 'hungary', 'poland', 'romania', 'singapore', 'malaysia', 'philippines', 'cambodia', 'laos',
    'myanmar', 'nepal', 'sri lanka', 'maldives', 'fiji', 'tahiti', 'hawaii', 'caribbean'];
  const commonWords = ['hotel', 'suites', 'resort', 'inn', 'lodge', 'spa', 'the', 'and', 'at', 'by',
    'grand', 'royal', 'palace', 'tower', 'plaza', 'park', 'city', 'beach', 'mountain', 'lake', 'bay',
    'island', 'center', 'centre', 'north', 'south', 'east', 'west', 'new', 'old', 'upper', 'lower'];
  const hotelKeyWords = hotelLower.split(/[\s-]+/).filter(w =>
    w.length > 4 &&
    !commonWords.includes(w) &&
    !baseCityWords.includes(w) &&
    !countryNames.includes(w)
  );

  // Fallback: try shorter words that aren't super common
  if (hotelKeyWords.length === 0) {
    const fallbackWords = hotelLower.split(/[\s-]+/).filter(w =>
      w.length > 2 && !['the', 'and', 'at', 'by', 'in', 'on'].includes(w) &&
      !baseCityWords.includes(w) && !countryNames.includes(w)
    );
    if (fallbackWords.length > 0) {
      hotelKeyWords.push(...fallbackWords);
    }
  }

  for (const word of hotelKeyWords) {
    if (locLower.includes(word) || restLower.includes(word) || addrLower.includes(word)) return true;
    // Also check singular/plural variants (berkshires↔berkshire)
    const variant = word.endsWith('s') ? word.slice(0, -1) : word + 's';
    if (variant.length > 4 && (locLower.includes(variant) || restLower.includes(variant) || addrLower.includes(variant))) return true;
  }

  // Special handling for Aman properties
  if (hotelLower.includes('aman')) {
    if (locLower.includes('aman') || restLower.includes('aman')) return true;
    const amanPropertyMatch = hotelLower.match(/aman\s*(\w+)/i);
    if (amanPropertyMatch) {
      const propertyName = amanPropertyMatch[1].toLowerCase();
      if (locLower.includes(propertyName) || restLower.includes(propertyName)) return true;
    }
  }

  // Check for luxury resort on-site facility patterns
  const isLuxuryResort = hotelLower.includes('aman') || hotelLower.includes('miraval') ||
                          hotelLower.includes('four seasons') || hotelLower.includes('ritz') ||
                          hotelLower.includes('canyon ranch') || hotelLower.includes('six senses') ||
                          hotelLower.includes('banyan tree') || hotelLower.includes('mandarin') ||
                          hotelLower.includes('peninsula') || hotelLower.includes('rosewood');

  if (isLuxuryResort) {
    const firstKeyWord = hotelKeyWords[0];
    if (firstKeyWord && (locLower.includes(firstKeyWord) || restLower.includes(firstKeyWord))) {
      return true;
    }

    const onSiteFacilities = ['hotel spa', 'hotel pool', 'hotel gym', 'hotel restaurant', 'hotel bar', 'hotel lounge', 'hotel breakfast', 'resort spa', 'resort pool', 'resort restaurant'];
    for (const facility of onSiteFacilities) {
      if (locLower.includes(facility) || restLower.includes(facility)) return true;
    }
  }

  // Check if restaurant/activity name starts with "Hotel"
  if (restLower.startsWith('hotel ') || actLower.startsWith('hotel ')) return true;

  // Check if location mentions "hotel" and same brand
  if (locLower.includes('hotel')) {
    const hotelBrands = ['four seasons', 'marriott', 'hilton', 'hyatt', 'sheraton', 'westin', 'ritz', 'mandarin', 'peninsula', 'park hyatt', 'st. regis', 'w hotel', 'intercontinental', 'sofitel', 'fairmont', 'rosewood', 'aman', 'raffles', 'novotel', 'ibis', 'radisson', 'doubletree', 'embassy suites', 'hampton', 'holiday inn', 'crowne plaza', 'jw marriott', 'le meridien', 'renaissance', 'autograph', 'tribute', 'aloft', 'element', 'moxy', 'ac hotel', 'aressana', 'miraval', 'canyon ranch'];
    for (const brand of hotelBrands) {
      if (hotelLower.includes(brand) && (locLower.includes(brand) || restLower.includes(brand))) return true;
    }
  }

  // Check if restaurant name contains hotel name words
  if (restLower && hotelLower) {
    const hotelWords = hotelLower.split(/\s+/).filter(w => w.length > 3 && !['hotel', 'suites', 'resort', 'inn'].includes(w));
    for (const word of hotelWords) {
      if (restLower.includes(word)) return true;
    }
  }

  // Explicit hotel breakfast/restaurant
  if (actLower.includes('hotel breakfast') || actLower.includes('breakfast at hotel')) return true;
  if (actLower.includes('hotel restaurant') || actLower.includes('hotel dining')) return true;
  if (restLower.includes('hotel restaurant') || restLower.includes('hotel breakfast')) return true;

  // Hotel brand-specific restaurant names
  if (restLower.includes('seasons restaurant') && hotelLower.includes('four seasons')) return true;
  if ((restLower.includes('lobby') || restLower.includes('lounge')) && locLower.includes('hotel')) return true;

  // Empty location + breakfast = likely at hotel
  if (!location && actLower.includes('breakfast')) return true;

  return false;
};

// ============================================
// IS MISMATCHED CITY
// ============================================

/**
 * Checks if an activity/meal's location mentions a different city than the day's baseCity.
 *
 * @param {Object} item - Activity or meal object with location/address/restaurant fields
 * @param {string} baseCity - The day's base city
 * @returns {boolean} True if there's a city mismatch
 */
export const isMismatchedCity = (item, baseCity) => {
  const baseCityLower = (baseCity || '').toLowerCase().trim();
  if (!baseCityLower) return false;

  const location = (item.location || item.address || '').toLowerCase();
  const restaurant = (item.restaurant || '').toLowerCase();

  // Only check items that have a city in their location (comma-separated like "Place, City")
  const locationParts = location.split(',').map(p => p.trim());
  if (locationParts.length < 2) return false;

  // Extract the city part (usually second-to-last before country)
  const cityPart = locationParts.length >= 3 ? locationParts[locationParts.length - 2] : locationParts[locationParts.length - 1];

  // Check if location mentions a known city that's different from baseCity
  const baseCityFirstWord = baseCityLower.split(/[\s,]+/)[0];
  for (const city of KNOWN_CITIES) {
    if (city === baseCityFirstWord || baseCityLower.includes(city) || city.includes(baseCityFirstWord)) continue;
    if (cityPart.includes(city) || location.includes(`, ${city},`) || restaurant.includes(`, ${city}`)) {
      return true;
    }
  }

  return false;
};

// ============================================
// IS UNREALISTIC DAY TRIP
// ============================================

/**
 * Checks if an activity represents an unrealistic day trip (5+ hours from base city).
 *
 * @param {Object} item - Activity object with activity/location/description fields
 * @param {string} baseCity - The day's base city
 * @returns {boolean} True if the day trip is unrealistic
 */
export const isUnrealisticDayTrip = (item, baseCity) => {
  const baseCityLower = (baseCity || '').toLowerCase().trim();
  if (!baseCityLower) return false;

  const location = (item.location || item.address || '').toLowerCase();
  const activity = (item.activity || item.name || '').toLowerCase();
  const description = (item.description || '').toLowerCase();
  const combinedText = `${activity} ${location} ${description}`;

  // 1. Check against known distant location patterns
  for (const pattern of DISTANT_LOCATION_PATTERNS) {
    if (baseCityLower.includes(pattern.baseCity) || pattern.baseCity.includes(baseCityLower)) {
      for (const distantLoc of pattern.distantLocations) {
        if (combinedText.includes(distantLoc)) {
          return true;
        }
      }
    }
  }

  // 2. Extract mentioned destination from "Day trip to X" phrases
  const dayTripMatch = combinedText.match(/(?:day trip to|excursion to|visit to|drive to|trip to)\s+([a-z\s]+?)(?:,|\.|$|\s+for|\s+to\s+see)/i);
  if (dayTripMatch) {
    const mentionedDestination = dayTripMatch[1].trim().toLowerCase();
    const pairKey1 = `${baseCityLower}-${mentionedDestination}`;
    const pairKey2 = `${mentionedDestination}-${baseCityLower}`;
    if (UNREALISTIC_DAY_TRIP_PAIRS.has(pairKey1) || UNREALISTIC_DAY_TRIP_PAIRS.has(pairKey2)) {
      return true;
    }
  }

  // 3. Cross-country day trips
  const baseCityCountry = CITY_TO_COUNTRY[baseCityLower];
  if (baseCityCountry) {
    for (const [city, country] of Object.entries(CITY_TO_COUNTRY)) {
      if (country !== baseCityCountry && combinedText.includes(city)) {
        const pairKey = `${baseCityLower}-${city}`;
        if (UNREALISTIC_DAY_TRIP_PAIRS.has(pairKey)) {
          return true;
        }
      }
    }
  }

  return false;
};
